import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { isDesktopDevice } from "./harvestEntryHelpers.js";

const FULLSCREEN_Z = 10000;
const DROPDOWN_Z = FULLSCREEN_Z + 1;
const GRID_MIN_HEIGHT = 520;

export function useHarvestEntryLayout({ gridWrapRef, sidebarOpen }) {
  const [gridWidth, setGridWidth] = useState(0);
  const [gridHeight, setGridHeight] = useState(null);

  // ---- fullscreen ----
  const [isNativeFullscreen, setIsNativeFullscreen] = useState(false);
  const [isPseudoFullscreen, setIsPseudoFullscreen] = useState(false);

  const allowNativeFullscreen = useMemo(() => isDesktopDevice(), []);
  const isDesktop = allowNativeFullscreen;

  const getFsElement = () =>
    document.fullscreenElement || document.webkitFullscreenElement || null;

  useEffect(() => {
    const onFsChange = () => {
      const active = Boolean(getFsElement());
      setIsNativeFullscreen(active);
      if (active) setIsPseudoFullscreen(false);
    };

    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);

    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
    };
  }, []);

  const isFullscreen = isNativeFullscreen || isPseudoFullscreen;

  const getGridPopupContainer = useCallback(() => {
    const el = gridWrapRef.current;
    if (isFullscreen && el) return el;
    return document.body;
  }, [isFullscreen, gridWrapRef]);

  const measureGridHeight = useCallback(() => {
    if (isFullscreen) return;
    const wrap = gridWrapRef.current;
    if (!wrap) return;

    const rect = wrap.getBoundingClientRect();
    const viewportH = window.innerHeight || 0;
    const footerEl = document.getElementById("app-footer");
    const footerH = footerEl?.getBoundingClientRect()?.height ?? 0;
    const parent = wrap.parentElement;
    const paddingBottom = parent
      ? parseFloat(window.getComputedStyle(parent).paddingBottom || "0") || 0
      : 0;
    const available = viewportH - rect.top - footerH - paddingBottom;
    const nextHeight = Math.max(GRID_MIN_HEIGHT, Math.floor(available));

    setGridHeight((prev) => (prev === nextHeight ? prev : nextHeight));
  }, [isFullscreen, gridWrapRef]);

  useLayoutEffect(() => {
    if (isFullscreen) {
      setGridHeight(null);
      return;
    }
    measureGridHeight();
  }, [isFullscreen, measureGridHeight, sidebarOpen]);

  useEffect(() => {
    if (isFullscreen) return;
    const handleResize = () => measureGridHeight();
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, [isFullscreen, measureGridHeight]);

  useEffect(() => {
    if (isFullscreen) return;
    const wrap = gridWrapRef.current;
    const parent = wrap?.parentElement;
    if (!parent) return;
    const ro = new ResizeObserver(() => measureGridHeight());
    ro.observe(parent);
    return () => ro.disconnect();
  }, [isFullscreen, measureGridHeight, gridWrapRef]);

  useEffect(() => {
    if (!gridWrapRef.current) return;

    const ro = new ResizeObserver((entries) => {
      const w = entries?.[0]?.contentRect?.width ?? 0;
      setGridWidth(w);
    });

    ro.observe(gridWrapRef.current);
    return () => ro.disconnect();
  }, [gridWrapRef]);

  useEffect(() => {
    document.body.style.overflow = isFullscreen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isFullscreen]);

  const exitPseudoFullscreen = useCallback(() => {
    setIsPseudoFullscreen(false);
  }, []);

  useEffect(() => {
    const el = gridWrapRef.current;
    if (!el) return;

    const measure = () => {
      const w = el.getBoundingClientRect?.().width ?? 0;
      setGridWidth(w);
    };

    requestAnimationFrame(measure); // immediate after React paints
    const t = setTimeout(measure, 120); // after fullscreen layout settles

    return () => clearTimeout(t);
  }, [isFullscreen, gridWrapRef]);

  const toggleFullscreen = useCallback(async () => {
    const el = gridWrapRef.current;
    if (!el) return;

    if (!allowNativeFullscreen) {
      setIsPseudoFullscreen((v) => !v);
      return;
    }

    try {
      const request =
        el.requestFullscreen ||
        el.webkitRequestFullscreen ||
        el.msRequestFullscreen;

      const exit =
        document.exitFullscreen ||
        document.webkitExitFullscreen ||
        document.msExitFullscreen;

      const fsEl = getFsElement();

      if (request && exit) {
        if (!fsEl) await request.call(el);
        else await exit.call(document);
        return;
      }

      setIsPseudoFullscreen((v) => !v);
    } catch (e) {
      console.error("Fullscreen toggle failed:", e);
      setIsPseudoFullscreen((v) => !v);
    }
  }, [allowNativeFullscreen, gridWrapRef]);

  return {
    gridWidth,
    gridHeight,
    isFullscreen,
    isPseudoFullscreen,
    toggleFullscreen,
    exitPseudoFullscreen,
    getGridPopupContainer,
    isDesktop,
    FULLSCREEN_Z,
    DROPDOWN_Z,
    GRID_MIN_HEIGHT,
  };
}
